import { useState } from 'react';
import { createPortal } from 'react-dom';

const BOARD_COLORS = ['#E94934', '#FFD92B', '#FFA40D', '#3B82F6', '#10B981', '#8B5CF6'];
const BOARD_ICONS = ['📚', '🥊', '✨', '🎬', '⭐', '🎯'];

function NewBoardForm({ onSubmit, onCancel }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [color, setColor] = useState(BOARD_COLORS[0]);
  const [icon, setIcon] = useState(BOARD_ICONS[0]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await onSubmit({ name: name.trim(), desc, color, emoji: icon });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#090909', marginBottom: '8px', letterSpacing: '-0.04em' }}>
          보드 이름 <span style={{ color: '#E94934' }}>*</span>
        </label>
        <input
          autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="예 : 관심 공고, 지원 예정"
          style={{ width: '100%', padding: '12px 14px', border: '1px solid #E9E9EC', borderRadius: '12px', fontSize: '15px', fontFamily: 'Pretendard, sans-serif', outline: 'none', boxSizing: 'border-box', letterSpacing: '-0.04em' }}
        />
      </div>

      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#090909', marginBottom: '8px', letterSpacing: '-0.04em' }}>
          설명
        </label>
        <textarea
          value={desc}
          onChange={e => setDesc(e.target.value)}
          placeholder="이 보드에 대한 간단한 설명을 입력하세요."
          style={{ width: '100%', height: '90px', padding: '12px 14px', border: '1px solid #E9E9EC', borderRadius: '12px', fontSize: '15px', fontFamily: 'Pretendard, sans-serif', outline: 'none', boxSizing: 'border-box', letterSpacing: '-0.04em', resize: 'none', lineHeight: '1.5' }}
        />
      </div>

      <div>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#090909', marginBottom: '10px', letterSpacing: '-0.04em' }}>색상</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {BOARD_COLORS.map(c => (
            <button key={c} onClick={() => setColor(c)}
              style={{ width: '40px', height: '40px', borderRadius: '10px', background: c, border: color === c ? '2px solid #090909' : '2px solid transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
              {color === c && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17l-5-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#090909', marginBottom: '10px', letterSpacing: '-0.04em' }}>아이콘</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {BOARD_ICONS.map(ic => (
            <button key={ic} onClick={() => setIcon(ic)}
              style={{ width: '40px', height: '40px', borderRadius: '10px', background: ic === icon ? '#f5f5f5' : '#fafafa', border: ic === icon ? '2px solid #090909' : '1px solid #E9E9EC', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
              {ic}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
        <button onClick={onCancel}
          style={{ flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid #E9E9EC', background: '#fff', cursor: 'pointer', fontSize: '15px', fontWeight: 600, fontFamily: 'Pretendard, sans-serif', color: '#090909', letterSpacing: '-0.04em' }}
        >
          취소
        </button>
        <button onClick={handleSubmit} disabled={!name.trim()}
          style={{ flex: 2, padding: '14px', borderRadius: '12px', border: 'none', background: name.trim() ? '#090909' : '#E9E9EC', cursor: name.trim() ? 'pointer' : 'default', fontSize: '15px', fontWeight: 600, fontFamily: 'Pretendard, sans-serif', color: name.trim() ? '#fff' : '#A2A2A2', letterSpacing: '-0.04em' }}
        >
          보드 만들기
        </button>
      </div>
    </div>
  );
}

// 저장 완료 확인 모달 — context의 savedConfirmState로 렌더됨
export function SavedConfirmModal({ boardName, onClose, onNavigateToArchive, zIndex = 99999 }) {
  return createPortal(
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '360px', background: '#fff', borderRadius: '20px', padding: '28px 24px', boxSizing: 'border-box' }}
      >
        <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: 700, color: '#090909', letterSpacing: '-0.04em' }}>저장 완료!</h4>
        <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#666', lineHeight: '1.6', letterSpacing: '-0.04em' }}>
          <strong style={{ color: '#090909' }}>{boardName}</strong> 보관함에 저장됐어요.<br />
          보관함으로 이동할까요?
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '13px', borderRadius: '12px', border: '1px solid #E9E9EC', background: '#fff', cursor: 'pointer', fontSize: '15px', fontWeight: 600, fontFamily: 'Pretendard, sans-serif', color: '#090909', letterSpacing: '-0.04em' }}
          >
            취소
          </button>
          <button onClick={() => { onClose(); onNavigateToArchive?.(); }}
            style={{ flex: 1, padding: '13px', borderRadius: '12px', border: 'none', background: '#090909', cursor: 'pointer', fontSize: '15px', fontWeight: 600, fontFamily: 'Pretendard, sans-serif', color: '#fff', letterSpacing: '-0.04em' }}
          >
            이동
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function BoardPickerModal({ boards, targetType, onClose, onSelect, onCreateAndSelect, zIndex = 99999 }) {
  const [showNewForm, setShowNewForm] = useState(false);
  const itemLabel = targetType === 'TALENT_PROFILE' ? '배우' : '공고';

  return createPortal(
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '460px', background: '#fff', borderRadius: '24px', padding: '32px 28px', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' }}
      >
        {showNewForm ? (
          <>
            <h3 style={{ margin: '0 0 24px 0', fontSize: '24px', fontWeight: 700, color: '#090909', letterSpacing: '-0.04em' }}>새 보드 만들기</h3>
            <NewBoardForm
              onSubmit={onCreateAndSelect}
              onCancel={() => setShowNewForm(false)}
            />
          </>
        ) : (
          <>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 700, color: '#090909', letterSpacing: '-0.04em' }}>보관함 선택</h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '15px', color: '#A2A2A2', lineHeight: '1.6', letterSpacing: '-0.04em' }}>저장할 보관함을 선택하세요.</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
              {boards.map(board => (
                <button key={board.id} type="button" onClick={() => onSelect(board.id)}
                  style={{ width: '100%', padding: '14px 16px', borderRadius: '14px', border: '1px solid #E9E9EC', background: '#fff', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: '10px', background: board.iconBackground || board.color || '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                    {board.emoji || board.icon || '📁'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: '#090909', letterSpacing: '-0.04em' }}>{board.name}</div>
                    <div style={{ marginTop: '2px', fontSize: '13px', color: '#A2A2A2', letterSpacing: '-0.04em' }}>
                      저장된 {itemLabel} {board.count ?? board.itemCount ?? 0}명
                    </div>
                  </div>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2v16z" stroke="#090909" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={onClose}
                style={{ flex: 1, padding: '14px', borderRadius: '12px', border: '1px solid #E9E9EC', background: '#fff', cursor: 'pointer', fontSize: '15px', fontWeight: 600, fontFamily: 'Pretendard, sans-serif', color: '#090909', letterSpacing: '-0.04em' }}
              >
                취소
              </button>
              <button onClick={() => setShowNewForm(true)}
                style={{ flex: 2, padding: '14px', borderRadius: '12px', border: 'none', background: '#090909', cursor: 'pointer', fontSize: '15px', fontWeight: 600, fontFamily: 'Pretendard, sans-serif', color: '#fff', letterSpacing: '-0.04em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <span style={{ fontSize: '18px', lineHeight: 1 }}>+</span>
                새 보드
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default BoardPickerModal;
